import { OnDestroy, ViewEncapsulation, Inject } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { map, catchError, finalize, tap } from 'rxjs/operators';
import { ColumnSorterInfo, ConfigScope, ConfigSettingType, DefaultPage, FieldType, FilterEvent, FilterName, FormatType, PageEvent, SearchEvent, SortEvent, TableConfig, TableEvent } from 'src/app/models/custom-table/custom-table.model';
import { PlaylistDTO } from 'src/app/models/playlists/playlistDTO';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { PlaylistsService } from 'src/app/shared/services/playlists/playlists.service';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { TableConfigService } from 'src/app/shared/services/table-config/table-config.service';
import { DayOfWeekEnum } from 'src/app/layout-modules/guides-Management/guide-detail/guide-detail.component';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';

const USAGE_CODE = "SELECT_PLAYLIST";

const FORM_PARAMS = {
  dayWeek: "dayWeek",
  timeStart: "timeStart",
  timeEnd: "timeEnd"
}

export function endTimeValidator(formGroup: AbstractControl): ValidationErrors | null {
  const initialHour = formGroup.parent?.get('timeStart')?.value;
  const endHour = formGroup.value;

  if(initialHour === null || endHour === null){
    return null;
  }

  if(endHour <= initialHour){
    return {  invalidEndTime: true }
  }

  return null;
}

@Component({
  selector: 'app-select-playlist-dialog',
  templateUrl: './select-playlist-dialog.component.html',
  styleUrls: ['./select-playlist-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectPlaylistDialogComponent implements OnInit, OnDestroy{
  public isLoading$ = new BehaviorSubject<boolean>(false);
  private loadingCounter = 0;

  public totalPlaylists$ = new BehaviorSubject<number>(0);
  public columnInfo$: Observable<ColumnSorterInfo[]>;
  public playlistsList: PlaylistDTO[];
  public playlistsSubscription: Subscription;
  
  daysOfWeek = Object.values(DayOfWeekEnum);
  hours = Array.from({ length: 25 }, (_, i) => i);

  public selectedRow: Object = {};

  playlistDataForm: FormGroup;
  
  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Playlist.id",
      field: "token",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier:true
    },
    {
      header: "Playlist.name",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Playlist.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Playlist.terminalType",
      field: "terminalType",
      fieldType: FieldType.STRING,
    },
    {
      header: "Playlist.lastModification",
      field: "updatedOn",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATETIME,
    }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private playlistsService: PlaylistsService,
    private dialogRef: MatDialogRef<SelectPlaylistDialogComponent>,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.columnInfo$ = this.getTableConfig();
    this.loadTableData();
    this.createPlaylistForm();

    if(this.data?.preDay){
      this.playlistDataForm.patchValue({
        dayWeek: [this.data.preDay],
        timeStart: this.data.preTimeStart,
        timeEnd: this.data.preTimeEnd
      });
    }
  }
  
  createPlaylistForm(){
    this.playlistDataForm = this.fb.group({
      [FORM_PARAMS.dayWeek]: [
        null,
        [Validators.required],
      ],
      [FORM_PARAMS.timeStart]:[
        null,
        [Validators.required]
      ],
      [FORM_PARAMS.timeEnd]:[
        null,
        [Validators.required, endTimeValidator]
      ],
    });

    this.playlistDataForm.get('timeStart')?.valueChanges.subscribe(()=>{
      this.playlistDataForm.get('timeEnd')?.updateValueAndValidity();
    });
  }

  loadTableData(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ){
    const filter: FilterEvent = !this.data?.terminalType ? 
      { advancedFilter: [] } :
      {advancedFilter: [
        {
          column: "terminalType",
          fieldType: FieldType.STRING,
          operator: FilterName.EQUALS,
          value: this.data.terminalType
        }
      ]};
      
      filter.advancedFilter = [
        ...filter.advancedFilter, 
        ...(filterEvent?.advancedFilter ?? [])
      ];

    const { pageIndex, pageSize } = pageEvent || {};
    this.setLoading(true);
    this.playlistsSubscription = this.playlistsService
      .getPlaylists(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filter
      )
      .pipe(
        map(({result}) => {
          this.totalPlaylists$.next(result.size);
          return result.items;
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("Playlist.playlistsLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe((result: any[]) => {
        this.playlistsList = result.map((playlist) => ({...playlist, code: playlist.token}));
      });
  }

  getTableConfig() {
    this.setLoading(true);
    return this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.LoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      );
  }

  onSelectedRow(event){
    const {row, dblClick} = event;
    this.selectedRow = row;
    if(dblClick){
      this.onConfirmClick();
    }
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.setLoading(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveSuccess"),
            type: SnackbarType.SUCCESS,
          })
        ),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("TableConfig.SaveError"),
            type: SnackbarType.SUCCESS,
          });
          return EMPTY;
        }),
        finalize(() => this.setLoading(false))
      )
      .subscribe();
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadTableData(pageEvent, sortEvent, searchEvent, filterEvent);
  }
  
  onConfirmClick() {
    if(!this.playlistDataForm.valid){
      this.snackbarService.show({
        message: this.translate.instant("Guide.guideFormInvalid"),
        type: SnackbarType.ERROR
      })
      return;
    }

    const selectedDays: string[] =
      this.playlistDataForm.get('dayWeek')?.value ?? [];

    const returnRows = selectedDays.map((day: string) => ({
      ...this.selectedRow,
      dayWeek: day, 
      timeStart: this.playlistDataForm.controls[FORM_PARAMS.timeStart].value, 
      timeEnd: this.playlistDataForm.controls[FORM_PARAMS.timeEnd].value
    }));
    
    const schedule = this.data.schedule ?? {};
    
    const hasConflict = selectedDays.some(day => {
      const scheduleDay = schedule[day];
      if (!scheduleDay) return false;

      const newRow = returnRows.find(row => row.dayWeek === day);
      const newStart = newRow?.timeStart ?? 0;
      const newEnd   = newRow?.timeEnd ?? 0;

      const hoursToOccupy = Array.from(
        { length: newEnd - newStart },
        (_, i) => newStart + i
      );

      return Object.keys(scheduleDay).some(hour =>
        scheduleDay[hour].some(view => {
          const existingStart = parseInt(view.timeStart.split(':')[0], 10);
          return hoursToOccupy.includes(existingStart);
        })
      );
    });

    if (hasConflict) {
      this.snackbarService.show({
        message: this.translate.instant("Guide.slotOccupied"),
        type: SnackbarType.ERROR
      });
      return;
    }

    this.dialogRef.close(returnRows);
  }

  public isSelectedRowEmpty(): boolean {
    return Object.keys(this.selectedRow).length === 0;
  }

  private setLoading(loading: boolean) {
    this.loadingCounter += loading ? 1 : -1;
    this.isLoading$.next(this.loadingCounter > 0);
  }

  ngOnDestroy(): void {
    if (this.playlistsSubscription) {
      this.playlistsSubscription.unsubscribe();
    }
  }
}
